/**
 * Product Information Novo — galeria, barra de progresso e modal de zoom.
 *
 * O DIMENSIONAMENTO VIVE NO CSS (--pin-slide-size). Este arquivo apenas:
 *   1. entrega ao Swiper o único token de gap (--pin-gap), para que o espaço
 *      entre slides seja exatamente o mesmo do resto do layout;
 *   2. publica a altura natural do painel em --pin-panel-height — é ela que
 *      define a altura (e, sendo a mídia 1:1, a largura) do slide no modo
 *      sobreposto, para a galeria terminar exatamente onde o painel termina;
 *   3. garante que TODA mídia consiga aparecer na faixa livre, mesmo a que
 *      está embaixo do painel, e que o carrossel sempre dê a volta: com mídias
 *      suficientes, loop contínuo; sem elas, slidesOffsetAfter (para a última
 *      sair de baixo do painel) mais rewind (para voltar à primeira);
 *   4. liga setas, segmentos de progresso e o modal de zoom.
 *
 * Nenhum breakpoint com valores diferentes de slidesPerView/spaceBetween: o
 * gap é sempre o mesmo e a quantidade de slides visíveis se ajusta sozinha,
 * porque é o tamanho do slide que muda.
 */
(function () {
    'use strict';

    var SELECTOR = '.product-info-novo__wrapper';

    /* ---------------------------------------------------------------- utils */

    function cssNumber(element, property, fallback) {
        var raw = getComputedStyle(element).getPropertyValue(property).trim();
        var value = parseFloat(raw);
        return isFinite(value) ? value : fallback;
    }

    function initSection(wrapper) {
        if (!wrapper || wrapper.dataset.pinInitialized === 'true') return;
        if (typeof Swiper === 'undefined') return;

        var gallery = wrapper.querySelector('[data-product-gallery-swiper]');
        if (!gallery) return;

        wrapper.dataset.pinInitialized = 'true';

        var details = wrapper.querySelector('.product-info-novo__details');
        var detailsContent = wrapper.querySelector('.product-info-novo__details-content');
        var prevBtn = wrapper.querySelector('[data-gallery-prev]');
        var nextBtn = wrapper.querySelector('[data-gallery-next]');
        var segments = Array.prototype.slice.call(wrapper.querySelectorAll('[data-progress-segment]'));

        var mediaCount = parseInt(wrapper.dataset.mediaCount, 10) || 0;
        var stackBelow = parseInt(wrapper.dataset.stackBelow, 10) || 990;

        // Os dois únicos números de layout — lidos uma vez, iguais em toda tela.
        var gap = cssNumber(wrapper, '--pin-gap', 16);

        var swiper = null;
        var looping = false;

        /* ------------------------------------------------- altura do painel */

        var lastPanelHeight = null;

        function isOverlay() {
            return window.innerWidth >= stackBelow;
        }

        /**
         * Publica a altura natural do painel — a medida que dimensiona o slide
         * no modo sobreposto. Medimos o conteúdo interno (e não o painel) de
         * propósito: o painel nunca recebe altura imposta, então a medida não
         * realimenta o próprio valor.
         *
         * No modo empilhado a propriedade é REMOVIDA em vez de zerada, para o
         * CSS voltar ao fallback por largura (var(--pin-panel-height, ...) só
         * usa o fallback quando a propriedade não existe).
         */
        function syncPanelHeight() {
            var next = null;

            if (isOverlay() && detailsContent) {
                next = Math.ceil(detailsContent.getBoundingClientRect().height);
            }

            if (next === null) {
                if (lastPanelHeight === null) return false;
                lastPanelHeight = null;
                wrapper.style.removeProperty('--pin-panel-height');
                return true;
            }

            if (lastPanelHeight !== null && Math.abs(next - lastPanelHeight) <= 1) return false;

            lastPanelHeight = next;
            wrapper.style.setProperty('--pin-panel-height', next + 'px');
            return true;
        }

        /* ---------------------------------------------------------- galeria */

        function slideWidth() {
            var slide = gallery.querySelector('.swiper-slide');
            return slide ? slide.getBoundingClientRect().width : 0;
        }

        /**
         * Quanto da galeria fica coberto pelo painel — é a parte em que uma
         * mídia existe mas não pode ser vista. 0 no modo empilhado.
         */
        function stripOffset() {
            if (!isOverlay() || !details) return 0;
            return Math.ceil(details.getBoundingClientRect().width) + gap;
        }

        /**
         * Mesma contagem que o Swiper faz internamente (slidesPerViewDynamic):
         * quantos slides encostam na área do carrossel — inclusive os que estão
         * embaixo do painel, porque para o Swiper eles contam.
         */
        function slidesPerViewDynamic() {
            var width = slideWidth();
            if (!width) return 1;
            return Math.max(1, Math.ceil(gallery.clientWidth / (width + gap)));
        }

        /**
         * O loop CONTÍNUO do Swiper 12 só funciona com pelo menos
         * (slides visíveis + 1) mídias: ele reposiciona slides reais, então
         * precisa de pelo menos um sobrando para tapar o buraco enquanto o
         * primeiro volta para o fim. Com menos que isso ele avisa no console,
         * desliga o loop e o carrossel salta — por isso, abaixo desse limite,
         * usamos `rewind` (ver buildGallery) em vez de insistir no loop.
         *
         * Como a largura do slide é fluida, a decisão é recalculada a cada
         * resize.
         */
        function shouldLoop() {
            if (mediaCount < 2) return false;
            return mediaCount >= slidesPerViewDynamic() + 1;
        }

        function updateProgress(activeIndex) {
            segments.forEach(function (segment, index) {
                segment.classList.toggle('is-active', index === activeIndex);
                segment.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
            });
        }

        function buildGallery(startIndex) {
            looping = shouldLoop();

            swiper = new Swiper(gallery, {
                // A largura vem do CSS (--pin-slide-size), então o gap visual
                // é exatamente spaceBetween em qualquer resolução.
                slidesPerView: 'auto',
                spaceBetween: gap,
                loop: looping,
                // Sem mídias suficientes para o loop contínuo, o carrossel
                // ainda tem de dar a volta: no fim, "próximo" retorna à
                // primeira imagem (e "anterior" na primeira vai para a
                // última). É o loop possível quando não há slide sobrando.
                rewind: !looping,
                // Sem loop o carrossel pararia com as últimas mídias presas
                // embaixo do painel: esta folga no fim (do tamanho exato do
                // painel) deixa a última mídia chegar à faixa livre. Com loop
                // ela é desnecessária — e o Swiper não a suporta bem.
                slidesOffsetAfter: looping ? 0 : stripOffset(),
                loopAddBlankSlides: false,
                grabCursor: true,
                speed: 400,
                initialSlide: startIndex || 0,
                watchSlidesProgress: true,
                a11y: { enabled: false },
                on: {
                    slideChange: function () {
                        updateProgress(this.realIndex);
                    },
                },
            });

            updateProgress(swiper.realIndex || 0);
        }

        /**
         * Trocar o loop exige reconstruir o Swiper; a folga do fim é só um
         * parâmetro, então basta atualizá-la no lugar.
         */
        function refreshGeometry() {
            if (!swiper || swiper.destroyed) return;

            if (shouldLoop() !== looping) {
                var index = swiper.realIndex || 0;
                swiper.destroy(true, true);
                buildGallery(index);
                return;
            }

            var offset = looping ? 0 : stripOffset();
            if (swiper.params.slidesOffsetAfter !== offset) {
                swiper.params.slidesOffsetAfter = offset;
                swiper.update();
            }
        }

        function goTo(index) {
            if (!swiper || swiper.destroyed) return;
            if (looping) {
                swiper.slideToLoop(index);
            } else {
                swiper.slideTo(index);
            }
        }

        syncPanelHeight();
        buildGallery(0);

        if (prevBtn) {
            prevBtn.addEventListener('click', function () {
                if (swiper && !swiper.destroyed) swiper.slidePrev();
            });
        }

        if (nextBtn) {
            nextBtn.addEventListener('click', function () {
                if (swiper && !swiper.destroyed) swiper.slideNext();
            });
        }

        segments.forEach(function (segment) {
            segment.addEventListener('click', function () {
                goTo(parseInt(segment.dataset.progressSegment, 10) || 0);
            });
        });

        /* ----------------------------------------------------------- resize */

        var frame = null;

        function scheduleRefresh() {
            if (frame) cancelAnimationFrame(frame);
            frame = requestAnimationFrame(function () {
                frame = null;
                var changed = syncPanelHeight();
                if (changed && swiper && !swiper.destroyed) swiper.update();
                refreshGeometry();

                // O modal vive fora do wrapper e o Swiper dele nem sempre
                // percebe a mudança de viewport sozinho — o slide fica preso
                // na largura antiga. Atualizamos na mão.
                if (zoomSwiper && !zoomSwiper.destroyed) zoomSwiper.update();
                if (zoomThumbsSwiper && !zoomThumbsSwiper.destroyed) zoomThumbsSwiper.update();
            });
        }

        window.addEventListener('resize', scheduleRefresh);
        window.addEventListener('orientationchange', scheduleRefresh);
        window.addEventListener('load', scheduleRefresh);

        // O painel muda de altura ao trocar de variante / abrir um accordion.
        if (typeof ResizeObserver !== 'undefined' && detailsContent) {
            new ResizeObserver(scheduleRefresh).observe(detailsContent);
        }

        /* ------------------------------------------------------ modal de zoom */

        var zoomModal = wrapper.querySelector('[data-zoom-modal]');
        if (!zoomModal) return;

        var zoomSlider = zoomModal.querySelector('[data-zoom-swiper]');
        var zoomThumbs = zoomModal.querySelector('[data-zoom-thumbs-swiper]');
        var zoomClose = zoomModal.querySelector('[data-zoom-close]');
        var zoomNext = zoomModal.querySelector('[data-zoom-next]');
        var zoomPrev = zoomModal.querySelector('[data-zoom-prev]');
        var zoomable = wrapper.querySelectorAll('.product-info-novo__image, .product-info-novo__video');

        if (!zoomSlider) return;

        // Um modal órfão pode ter sobrado de um reload no editor de temas.
        var owner = zoomModal.getAttribute('data-zoom-owner');
        Array.prototype.forEach.call(
            document.querySelectorAll('[data-zoom-owner="' + owner + '"]'),
            function (stale) {
                if (stale !== zoomModal) stale.remove();
            }
        );

        // Fora do wrapper o modal não herda mais nenhum --pin-*, então os
        // tokens configurados pelo lojista viajam junto, como estilo inline.
        var inherited = getComputedStyle(wrapper);
        [
            '--pin-gap',
            '--pin-arrow-size',
            '--pin-arrow-bg',
            '--pin-arrow-border',
            '--pin-border-radius',
        ].forEach(function (token) {
            var value = inherited.getPropertyValue(token).trim();
            if (value) zoomModal.style.setProperty(token, value);
        });

        // Move para o body para escapar de qualquer contexto de empilhamento.
        if (zoomModal.parentNode !== document.body) {
            document.body.appendChild(zoomModal);
        }

        var zoomThumbsSwiper = null;

        if (zoomThumbs) {
            zoomThumbsSwiper = new Swiper(zoomThumbs, {
                slidesPerView: 'auto',
                spaceBetween: gap,
                freeMode: true,
                watchSlidesProgress: true,
                centerInsufficientSlides: true,
            });
        }

        var zoomSwiper = new Swiper(zoomSlider, {
            slidesPerView: 1,
            spaceBetween: 0,
            loop: mediaCount > 1,
            loopAddBlankSlides: false,
            grabCursor: true,
            speed: 400,
            navigation: { nextEl: zoomNext, prevEl: zoomPrev },
            thumbs: zoomThumbsSwiper ? { swiper: zoomThumbsSwiper } : undefined,
        });

        function openZoom(index) {
            zoomSwiper.update();
            if (zoomThumbsSwiper) zoomThumbsSwiper.update();

            if (mediaCount > 1) {
                zoomSwiper.slideToLoop(index, 0);
            } else {
                zoomSwiper.slideTo(index, 0);
            }
            zoomModal.classList.add('is-active');
            document.body.style.overflow = 'hidden';
        }

        function closeZoom() {
            zoomModal.classList.remove('is-active');
            document.body.style.overflow = '';
        }

        Array.prototype.forEach.call(zoomable, function (element) {
            element.style.cursor = 'zoom-in';
            element.addEventListener('click', function (event) {
                event.preventDefault();

                var slide = element.closest('.swiper-slide');
                var index = 0;

                if (slide) {
                    if (slide.hasAttribute('data-swiper-slide-index')) {
                        index = parseInt(slide.getAttribute('data-swiper-slide-index'), 10) || 0;
                    } else {
                        index = Array.prototype.indexOf.call(slide.parentNode.children, slide);
                    }
                }

                openZoom(index);
            });
        });

        if (zoomClose) zoomClose.addEventListener('click', closeZoom);

        zoomModal.addEventListener('click', function (event) {
            if (event.target.closest('.product-info-novo__zoom-nav')) return;
            if (event.target.closest('.product-info-novo__zoom-thumbs')) return;
            if (event.target.closest('.product-info-novo__zoom-close')) return;

            if (
                event.target === zoomModal ||
                event.target.classList.contains('product-info-novo__zoom-slide') ||
                event.target.classList.contains('swiper-wrapper')
            ) {
                closeZoom();
            }
        });

        document.addEventListener('keydown', function (event) {
            if (event.key === 'Escape' && zoomModal.classList.contains('is-active')) {
                closeZoom();
            }
        });
    }

    function initAll(root) {
        var scope = root && root.querySelectorAll ? root : document;
        Array.prototype.forEach.call(scope.querySelectorAll(SELECTOR), initSection);
        if (root && root.matches && root.matches(SELECTOR)) initSection(root);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () {
            initAll();
        });
    } else {
        initAll();
    }

    // Editor de temas: a seção é re-renderizada sem recarregar a página.
    document.addEventListener('shopify:section:load', function (event) {
        initAll(event.target);
    });

    document.addEventListener('shopify:section:unload', function (event) {
        var wrapper = event.target.querySelector
            ? event.target.querySelector(SELECTOR)
            : null;
        if (!wrapper) return;

        var owner = wrapper.getAttribute('data-section-id');
        Array.prototype.forEach.call(
            document.querySelectorAll('[data-zoom-owner="' + owner + '"]'),
            function (modal) {
                modal.remove();
            }
        );
        document.body.style.overflow = '';
    });
})();